import type { ReactNode } from 'react';

// Shared gradient-banner + avatar-initial header, used as the FIRST child
// inside a Modal's body (with the Modal rendered `hideHead`) instead of the
// plain title/subtitle bar — the "nice colored header" look the Entity
// Detail modal introduced, now reused across every record-style modal
// (Entity Detail/Add/Edit, Add/Edit Purchase Invoice, Record Payment) so
// they read as one consistent design instead of each having its own header
// treatment. Sticky within the modal body's own scroll container (top: -20
// — not 0, per explicit design direction) so the identity stays visible
// while the rest of a long form/detail scrolls underneath it. The info row
// has an opaque `var(--surface)` background — load-bearing, not decorative:
// without it, scrolled content shows through behind the pinned banner.
export function ModalBanner({ title, eyebrow, eyebrowColor, right, onClose, noGradient }: {
  /** Primary identity — company/supplier name, or a fallback like "New Entity". */
  title: string;
  /** Small uppercase category label under the title (e.g. "Add Entity", "Record Payment"). */
  eyebrow: string;
  /** Overrides the eyebrow's default var(--primary) — e.g. var(--danger)/var(--success)
   *  when the category itself carries a semantic direction (Bank's Expense/Income entry). */
  eyebrowColor?: string;
  /** Extra content (e.g. a Lot No pill) placed before the close button. */
  right?: ReactNode;
  onClose: () => void;
  /** Drops the 42px colored gradient band and the avatar's overlap-into-it
   *  offset — same title/eyebrow/avatar/actions layout, just compact, for a
   *  modal (e.g. Memo Out's Consignment preview, which already sits beside
   *  a wide document + side panel) where the full banner ate too much
   *  vertical space. Kept as an opt-in per-instance prop rather than a
   *  global size change, since every other modal using ModalBanner still
   *  wants the full-height version. */
  noGradient?: boolean;
}) {
  const initial = title.trim().charAt(0).toUpperCase() || '?';
  return (
    <div style={{ margin: '-20px -24px 20px', position: 'sticky', top: -20, zIndex: 2 }}>
      {!noGradient && (
        <div style={{
          height: 42,
          background: 'linear-gradient(135deg, color-mix(in srgb, var(--primary) 60%, var(--surface)), color-mix(in srgb, var(--primary) 18%, var(--surface-2)))',
        }} />
      )}
      <div style={{
        padding: noGradient ? '14px 24px' : '0 24px 10px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12,
        background: 'var(--surface)', borderBottom: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, minWidth: 0 }}>
          <div style={{
            width: noGradient ? 40 : 52, height: noGradient ? 40 : 52, borderRadius: '50%', flexShrink: 0,
            marginTop: noGradient ? 0 : -26,
            // Mixed against var(--surface), not var(--primary-light) — the
            // latter is mixed against `transparent` in the dark theme (see
            // index.css), which let both the gradient above and the surface
            // below show through unevenly here since this circle straddles
            // both. This mix is opaque in both themes.
            background: 'color-mix(in srgb, var(--primary) 20%, var(--surface))', color: 'var(--primary)',
            border: noGradient ? 'none' : '3px solid var(--surface)',
            boxShadow: noGradient ? 'none' : '0 2px 8px rgba(15,23,42,0.14)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: noGradient ? 15 : 20, fontWeight: 800,
          }}>
            {initial}
          </div>
          <div style={{ paddingBottom: noGradient ? 0 : 2, minWidth: 0 }}>
            <div style={{ fontSize: noGradient ? 15 : 17, fontWeight: 800, color: 'var(--text-1)', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
              {title}
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: eyebrowColor ?? 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: noGradient ? 2 : 3 }}>
              {eyebrow}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: noGradient ? 0 : 2, flexShrink: 0 }}>
          {right}
          <button type="button" onClick={onClose} className="modal-icon-btn" title="Close">
            <IconCloseSmall />
          </button>
        </div>
      </div>
    </div>
  );
}

function IconCloseSmall() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M2.5 2.5l9 9M11.5 2.5l-9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>;
}
